<template lang="pug">
  .card-container.column.relative(v-if='!!resource' :class="{'no-margin': !!noMargin}")
    .overlay-disabled.absolute.card-container.no-margin(v-if='disabled')
    .card-info.item-1.relative.column.space-between
      .color-tag(:style='setTagColor()' :class='{krea: isKrea, disabled}')
      .column.info-container
        .row.item-1.end.middle.m-bottom-05
          .row.middle(v-if='languages.length')
            button.row.middle.m-right-1.add-language(v-if='disabled' @click='addResourceLanguage')
              h2.text-small.bold {{ innerI18n.t("resourceCard.addLanguage", {locale: localeToAdd.label}) }}
              .bg-button.row.middle.center.m-left-05
                simple-svg.white-icon(:filepath='addIcon' width='12px' height='12px')
            img.m4-right-2.img-size(
              v-for='language in languages'
              :src='language.icon'
              :alt='innerI18n.t(`languages.${language.translation}`)'
              :title='innerI18n.t(`languages.${language.translation}`)'
            )
        router-link.item-4.text-big.black.bold.card-title.m4-bottom-2.text-left(:to='getResourceLink')
          | {{ resource.name || innerI18n.t('resourceCard.untitled') }}
        .row.item-1.m-bottom-1(v-if='resource.ageRange || resource.secondaryAgeRange')
          span.text-small.age-desc(:style='setAgeColor(resource.ageRange.mainColor)')
            | {{ innerI18n.t('resourceCard.agesTag', { min: resource.ageRange.minAge, max: resource.ageRange.maxAge }) }}
          span.text-small.age-desc(v-if='resource.secondaryAgeRange' :style='setAgeColor(resource.secondaryAgeRange.mainColor)')
            | {{ innerI18n.t('resourceCard.agesTag', { min: resource.secondaryAgeRange.minAge, max: resource.secondaryAgeRange.maxAge}) }}
        .row.item-2.middle
          .row.middle(v-if='resource.educationalProgramName')
            simple-svg.blue-icon.m-right-1(:filepath='programIcon' :height='"20px"' :width='"20px"')
            span.text-small.blue {{ resource.educationalProgramName }}
    .card-footer.row.space-between
      .row.middle.item-1
        img.authors(:src='resourceAuthor.picture || defaultImage')
        span.text-small.dark-gray {{ $tc('resourceCard.by', resourceAuthor.name) }}
      button.icon-button(v-if='canDeleteResource' @click='showDeleteResourceModal')
        simple-svg.dark-gray-icon(:filepath='trashIcon' width='20px' height='20px')
</template>

<script>
import { mapActions, mapState } from 'vuex'
import programIcon from '@/assets/ic_programs.svg'
import addToFolderIcon from '@/assets/ic_folder-manage_option.svg'
import defaultImage from '@/assets/empty_profile.svg'
import { supportedLanguages } from '@/constants/languages'
import { routes } from '../../../router'
import addIcon from '@/assets/ic_add.svg'
import { innerI18n } from '../../../main'
import trashIcon from '@/assets/krea/ic_trash.svg'
import { allLanguages } from '../../../constants/languages'

export default {
  props: {
    resource: { type: Object, required: true },
    noMargin: { type: Boolean, value: false },
    activeLanguage: { type: String }
  },
  data() {
    return {
      trashIcon,
      addToFolderIcon,
      programIcon,
      defaultImage,
      addIcon,
      innerI18n
    }
  },
  computed: {
    ...mapState({
      locale: state => state.behavior.locale,
      innerLocale: state => state.behavior.innerLocale,
      educationalContent: state => state.kreaEducationalContent.content,
      categories: state => state.dashboard.categories
    }),
    canDeleteResource() {
      return this.educationalContent.educationalProgramResources.length > 1
    },
    languages() {
      return supportedLanguages.filter(language => Object.keys(this.resource.availableLanguages).includes(language.lang))
    },
    isKrea() {
      return this.resource.category === 'Krea'
    },
    resourceAuthor() {
      return this.resource.author || this.resource.mainAuthor
    },
    disabled() {
      return !Object.keys(this.resource.availableLanguages).includes(this.activeLanguage)
    },
    getResourceLink() {
      return !Object.keys(this.resource.availableLanguages).includes(this.locale)
        ? { name: routes.kreaResource, params: { id: this.resource.id }, query: { locale: this.activeLanguage || Object.keys(this.resource.availableLanguages)[0] } }
        : { name: routes.kreaResource, params: { id: this.resource.id }, query: { locale: this.activeLanguage } }
    },
    localeToAdd() {
      return supportedLanguages.find(lang => lang.lang === this.innerLocale)
    }
  },
  methods: {
    ...mapActions([
      'showElement',
      'changeInnerLocale',
      'addLanguageKreaResource',
      'hideElement'
    ]),
    getLanguageIcon(language) {
      return allLanguages.find(lang => lang.lang === language).icon
    },
    setTagColor() {
      return {
        '--tag-color': this.resource.categoryColor
      }
    },
    setAgeColor(color) {
      return {
        '--age-color': color || 'white'
      }
    },
    showDeleteResourceModal() {
      this.showElement({
        element: 'kreaDeleteModal',
        props: {
          id: this.resource.id,
          languages: Object.keys(this.resource.availableLanguages),
          type: 'resource'
        }
      })
    },
    async addResourceLanguage() {
      this.showElement({ element: 'fullPageLoader' })
      const success = await this.addLanguageKreaResource({ id: this.resource.id, locale: this.localeToAdd.lang })
      if (success) this.$router.push({ name: routes.kreaResource, params: { id: this.resource.id }, query: { locale: this.localeToAdd.lang } })
      this.hideElement({ element: 'fullPageLoader' })
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.card-container{
  height: 260px;
  box-shadow: 0px 5px 4px 0px $shadow-gray1;
}

.overlay-disabled{
  background-color: $light-gray;
  opacity: 0.6;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.card-container.no-margin {
  margin-left: 0;
}

.add-language{
  z-index: 3;
  .bg-button{
    height: 18px;
    width: 18px;
    border-radius: 50%;
    background-color: $blue;
  }
}

.color-tag {
  background-color: var(--tag-color);
  border-radius: 4px;
  height: 120px;
  max-width: 48px;
  width: 7px;
  position: absolute;
  left: 0;
  top: 50%;
  border: none;
  z-index: 2;
  transform: translate(-50%, -50%);
  box-shadow: 0px 0px 9px 5px $shadow-gray;
  -webkit-box-shadow: 0px 0px 9px 5px $shadow-gray;
  -moz-box-shadow: 0px 0px 9px 5px $shadow-gray;
  &.krea{
    background: linear-gradient($yellow 33%, $red 33%, $red 66%, $blue 66%);
  }
  &.disabled{
    &::after{
      content: "";
      width: 100%;
      height: 100%;
      border-radius: 4px;
      background-color: $light-gray;
      z-index: 3;
      opacity: 0.7;
      position: absolute;
      left: 0;
      top: 50%;
      transform: translate(0, -50%);
    }
  }
}

.color-tag-disabled {
  background-color: $light-gray;
  opacity: 0.8;
  border-radius: 4px;
  height: 120px;
  max-width: 48px;
  width: 7px;
  position: absolute;
  left: 0;
  top: 50%;
  border: none;
  z-index: 3;
}

.card-info {
  display: flex;
  flex-direction: column;
  padding: 18px;
  width: 100%;
  .info-container{
    height: 100%;
  }
  .age-desc {
    padding-left: 16px;
    position: relative;

    &:not(:last-child) {
      margin-right: 16px;
    }

    &::before {
      background-color: var(--age-color);
      border-radius: 100%;
      content: '';
      height: 8px;
      left: 0;
      position: absolute;
      top: 6px;
      width: 8px;
    }
  }
}

.read-text {
  font-size: 12px;
}

.card-title {
  max-width: 196px;
}

.card-footer {
  align-items: center;
  border-top: 1px solid $light-gray;
  display: flex;
  justify-content: flex-start;
  height: 50px;
  padding: 0 12px;
  width: 100%;
  .authors{
    height: 30px;
    width: 30px;
  }
}

.bookmark {
  height: 25px;
  width: 25px;
}

.m-bottom-12 {
  margin-bottom: 12px;
}

.img-size {
  width: 20px;
}
</style>
